import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';
import { AsyncSubject, Observable, Subject, Subscriber } from 'rxjs';

import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FileItem, FileType } from '../models/file-item.model';
import { DocRebootSupportMode, HostInitializeDiagnostics, IsFeatureEnabledCallback, NewFileInfo, OfficeApplicationCustomSettings, OfficeBootstrapper, OfficeBootstrapperContainer, OfficeBootstrapperFailureCallback, OfficeBootstrapperInitParams, OfficeBootstrapperSessionInfo, OfficeBootstrapperSuccessCallback, OfficeBootstrapperWopiPrecheckInfo, RebootOverrideData, WopiInfo } from '../models/jsapi.model';
import { Wopi } from '../models/onedrive-graph.model';
import { BootAndLoadInfo, BootedDocumentInfo, DocumentInfo, WacContentPerformanceMarker, WacHostBootError, WacHostBootErrorEvent, WacLoadData, WacLoadFunction, WacLoadParams, WacPendingLoadData, WacPerformanceTiming, WopiFetchData } from '../models/wac-host.model';
import { WopiHostData } from '../mru/mru-file-item.model';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { TemplatesConfigService } from '../templates/templates-config.service';
import { GuidGenerator } from '../utilities/guid-generator.utility';
import { getDeferredPromise } from '../utilities/promise-deffered.utility';
import { UrlBuilder } from '../utilities/url-builder.utility';
import { UrlParser } from '../utilities/url-parser.utility';
import { createWacContentInstrumentationParams } from '../utilities/wac-content-utility';
import { DocumentService } from './document.service';
import { PerformanceService } from './performance.service';
import { PrebootedApplication, UiHostBootstrapperService } from './ui-host-bootstrapper.service';
import { VroomService } from './vroom.service';
import { WacPrebootService } from './wac-preboot.service';
import { WebPwaService } from './web-pwa.service';
import { WindowService } from './window.service';

@Injectable()
export class WacBootstrapperService extends UiHostBootstrapperService {
    private readonly prebootedApplications: Map<FileType, PrebootedApplication>;
    // TODO: Merge with bootstrapped documents
    private readonly bootstrappedDocuments: Map<number, BootAndLoadInfo>;
    private readonly prebootedDocuments: Map<string, number>;
    private readonly prebootedWopiFetchData: Map<string, Promise<WopiFetchData>>;
    private readonly prebootedSessionId: Map<string, string>;

    private static readonly uihitOfficeComPwa = 'pwa';
    private static readonly uihitOfficeComHwa = 'hwa';

    private uih: string;
    private uihit: string;
    private isStandalone: boolean;

    constructor(
        capabilitiesService: CapabilitiesService,
        clientStartupConfigService: ClientStartupConfigService,
        contextService: ContextService,
        documentService: DocumentService,
        instrumentationService: InstrumentationService,
        performanceService: PerformanceService,
        templatesConfigService: TemplatesConfigService,
        vroomService: VroomService,
        private readonly webPwaService: WebPwaService,
        private readonly windowService: WindowService,
        private readonly wacPrebootService: WacPrebootService
    ) {
        super(
            capabilitiesService,
            clientStartupConfigService,
            contextService,
            documentService,
            instrumentationService,
            performanceService,
            templatesConfigService,
            vroomService
        );
        this.prebootedApplications = new Map<FileType, PrebootedApplication>();
        this.bootstrappedDocuments = new Map<number, BootAndLoadInfo>();
        this.prebootedDocuments = new Map<string, number>();
        this.prebootedWopiFetchData = new Map<string, Promise<WopiFetchData>>();
        this.prebootedSessionId = new Map<string, string>();

        this.webPwaService.isStandalone.subscribe(isWebPwa => {
            this.isStandalone = isWebPwa;
            this.uihit = this.getUihit(isWebPwa);
            this.uih = `officecom${this.uihit}`;
        });
    }

    public prebootDocument(documentInfo: DocumentInfo) {
        if (!documentInfo.driveId || !documentInfo.docId || this.prebootedDocuments.has(this.getPrebootKey(documentInfo))) {
            return;
        }

        const wopiFetchPromise = this.getWopiData(documentInfo, false, false, true);
        wopiFetchPromise.then(wopiFetchData => {
            const sessionId = GuidGenerator.createGuid();
            const bootId = this.index++;
            const prebootKey = this.getPrebootKey(documentInfo);
            this.prebootedDocuments.set(prebootKey, bootId);
            this.prebootedWopiFetchData.set(prebootKey, wopiFetchPromise);
            this.prebootedSessionId.set(prebootKey, sessionId);
            const requestBody: string =
                'access_token=' + encodeURIComponent(wopiFetchData.wopiHostData.wac.accessToken) +
                '&access_token_ttl=' + encodeURIComponent(wopiFetchData.wopiHostData.wac.accessTokenExpiry) +
                '&bundleSrc=' + encodeURIComponent(wopiFetchData.wopiHostData.bundleUrl) +
                '&fileExtension=' + encodeURIComponent(FileItem.getFileExtension(wopiFetchData.wopiHostData.fileName)) +
                '&user_id=' + encodeURIComponent(wopiFetchData.wopiHostData.wac.userId);

            this.wacPrebootService.postPreboot(
                KnownInstrumentationEvents.WacDocumentPreboot,
                KnownRequestInstrumentationEvents.WacDocumentPreboot,
                this.getPrebootUrl(wopiFetchData.wopiHostData.wac.applicationUrl) + '?op=preboot&usid=' + encodeURI(sessionId),
                requestBody
            );
        });
    }

    public bootstrapItem(
        documentInfo: DocumentInfo,
        clickTime: number,
        pendingWopiFetchPromise?: Promise<WopiFetchData>
    ): BootAndLoadInfo {
        // TODO: Re-enabled prebooting non-new documents
        // Reset the list so we can get a fresh boot if they head back to the start page
        this.prebootedApplications.clear();

        let prebootedWopiFetchData;
        let existingBootId;
        let sessionId;
        let isHoverPrebootedFile = false;
        if (this.capabilitiesService.wacHostHoverPrebootEnabledForFileType(documentInfo.fileType) && documentInfo.driveId && documentInfo.docId) {
            const prebootKey = this.getPrebootKey(documentInfo);
            existingBootId = this.prebootedDocuments.get(prebootKey);
            prebootedWopiFetchData = this.prebootedWopiFetchData.get(prebootKey);
            sessionId = this.prebootedSessionId.get(prebootKey);
            isHoverPrebootedFile = Number.isInteger(existingBootId);
            if (isHoverPrebootedFile) {
                this.prebootedDocuments.delete(prebootKey);
                this.prebootedSessionId.delete(prebootKey);
            }
        }

        const [correlationId, bootId] = this.generateCorrelationAndBootId(existingBootId);
        this.logPerformanceBootStart(bootId, correlationId, documentInfo.wdOrigin, documentInfo.fileType);

        const wacLoadFunctionSubject = new AsyncSubject<WacLoadFunction>();

        this.logPerformanceEventFromClick(bootId, WacContentPerformanceMarker.DocumentWopiFetchStart, documentInfo.fileType);
        const wopiFetchPromise = prebootedWopiFetchData ?? (pendingWopiFetchPromise || this.getWopiData(documentInfo, false, false, true));

        wopiFetchPromise.then(wopiFetchData => {
            this.logPerformanceEventFromClick(bootId, WacContentPerformanceMarker.DocumentWopiFetchEnd, documentInfo.fileType);
            this.bootstrapFile(
                wacLoadFunctionSubject,
                bootId,
                documentInfo,
                wopiFetchData.wopiHostData,
                correlationId,
                clickTime,
                wopiFetchData.fetchDiagnostics,
                true,
                isHoverPrebootedFile ? sessionId : undefined
            );
        }).catch((error: any) => {
            this.sendBootError(KnownInstrumentationEvents.WacDocumentVroomError, error, wacLoadFunctionSubject);
        });

        const { fileName } = documentInfo;

        const data = {
            correlationId,
            bootstrapperObservable: wacLoadFunctionSubject.asObservable(),
            fileNamePromise: fileName
                ? Promise.resolve(FileItem.fileNameWithoutExtension(fileName))
                : wopiFetchPromise.then(wopiData => FileItem.fileNameWithoutExtension(wopiData.wopiHostData.fileName)),
            documentIdentifierPromise: wopiFetchPromise.then(wopiFetchData => wopiFetchData.fetchedDocumentInfo)
        };

        const bootedDocumentInfo: BootedDocumentInfo = {
            bootId,
            documentInfo
        };

        const loadData: BootAndLoadInfo = {
            correlationId,
            bootedDocumentInfo,
            data
        };

        this.bootstrappedDocuments.set(bootId, loadData);

        return loadData;
    }

    /**
     * Waits for a new document be inflated then loads it using the wac boostrapper script
     * @param documentInfo: Info needed to boot the new document in the UI host
     */
    public bootstrapZeroByteDocument(
        documentInfo: DocumentInfo,
        clickTime: number,
        createDataPromise: Promise<DocumentInfo>,
        templateId?: string,
        templateDriveId?: string
    ): BootAndLoadInfo {
        let bootedApplication = this.prebootedApplications.get(documentInfo.fileType);

        // Unset the application if the session has expired
        if (Date.now() > bootedApplication?.expirationTime) {
            bootedApplication = undefined;
            this.prebootedApplications.clear();
        }

        const [correlationId, bootId] = bootedApplication ? bootedApplication.correlationAndBoot : this.generateCorrelationAndBootId();
        this.logPerformanceBootStart(bootId, correlationId, documentInfo.wdOrigin, documentInfo.fileType);

        const wacLoadFunctionSubject = bootedApplication ? bootedApplication.loadFunctionSubject : new AsyncSubject<WacLoadFunction>();

        // Right now the wopi data returned with the create new call does not work for booting the document
        // To mitigate this we make a follow up vroom call to fetch the wopi data
        const wopiFetchPromise = createDataPromise.then(info => {
            this.logPerformanceEventFromClick(bootId, WacContentPerformanceMarker.DocumentWopiFetchStart, documentInfo.fileType);
            return this.getWopiData(info, true, false, true);
        });

        wopiFetchPromise.then(async wopiFetchData => {
            this.logPerformanceEventFromClick(bootId, WacContentPerformanceMarker.DocumentWopiFetchEnd, documentInfo.fileType);

            const updatedDocumentInfo = await wopiFetchData.fetchedDocumentInfo;
            let applicationUrlParams: Params = null;

            if (templateDriveId) {
                applicationUrlParams = {
                    ...updatedDocumentInfo.applicationUrlParams,
                    templateItemId: templateId,
                    templateDriveId
                };
            } else if (templateId) {
                applicationUrlParams = {
                    ...updatedDocumentInfo.applicationUrlParams,
                    wdTpl: templateId
                };
            } else {
                applicationUrlParams = updatedDocumentInfo.applicationUrlParams;
            }

            if (bootedApplication) {
                bootedApplication.finishLoad(wopiFetchData.wopiHostData, updatedDocumentInfo.newFileInfo);
            } else {
                this.bootstrapFile(
                    wacLoadFunctionSubject,
                    bootId,
                    {
                        ...updatedDocumentInfo,
                        applicationUrlParams
                    },
                    wopiFetchData.wopiHostData,
                    correlationId,
                    clickTime,
                    { ...wopiFetchData.fetchDiagnostics, endTime: Date.now() }
                );
            }
        }).catch(error => {
            this.sendBootError(KnownInstrumentationEvents.WacDocumentVroomError, error, wacLoadFunctionSubject);
        });

        const documentInfoPromise = wopiFetchPromise.then(fetchData => fetchData.fetchedDocumentInfo);

        const data = {
            bootstrapperObservable: wacLoadFunctionSubject.asObservable(),
            fileNamePromise: documentInfoPromise.then(info => FileItem.fileNameWithoutExtension(info.fileName)),
            documentIdentifierPromise: documentInfoPromise
        };

        const bootedDocumentInfo: BootedDocumentInfo = {
            bootId,
            documentInfo: {
                ...documentInfo,
                newFile: true,
                templateId
            }
        };

        const loadData: BootAndLoadInfo = {
            correlationId,
            bootedDocumentInfo,
            data
        };

        this.bootstrappedDocuments.set(bootId, loadData);

        if (bootedApplication) {
            this.prebootedApplications.clear();
        }

        return loadData;
    }

    public bootstrapApplication(
        fileType: FileType,
        wdOrigin: string
    ): PrebootedApplication {
        if (!this.capabilitiesService.capabilities.wacHostPrebootApps.has(fileType) || this.prebootedApplications.has(fileType)) {
            return;
        }
        const [bootstrapper, error] = this.getOfficeBootstrapper(fileType);
        if (!bootstrapper || error) {
            return;
        }

        const [correlationId, bootId] = this.generateCorrelationAndBootId();
        const loadFunctionSubject = new AsyncSubject<WacLoadFunction>();

        const [wopiHostDataPromise, resolveWopiData, rejectWopiData] = getDeferredPromise<[WopiHostData, NewFileInfo | undefined]>();

        const loadApp = ({ container, flushTime, loadCorrelationId }: WacLoadParams) => new Observable<WacLoadData>(observer => {
            const initParams = this.getInitParams(
                fileType,
                loadCorrelationId,
                wdOrigin,
                container.nativeElement,
                flushTime,
                flushTime,
                async officeApplication => {
                    this.logPerformanceEventFromClick(bootId, WacContentPerformanceMarker.DocumentLoadEnd, fileType);
                    const [wopiHostData] = await wopiHostDataPromise;
                    observer.next({
                        wopiHostData,
                        officeApplication
                    });
                    observer.complete();
                },
                fail => {
                    this.sendBootError(KnownInstrumentationEvents.WacDocumentJsApiError, fail, observer);
                },
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                { wdasp: 1 }
            );

            this.logPerformanceEventFromClick(bootId, WacContentPerformanceMarker.DocumentInitializeWopiPending, fileType);
            const wopiPendingApp = bootstrapper.Application.InitializeWopiPending(initParams);

            if (wopiPendingApp) {
                wopiHostDataPromise.then(([wopiHostData, newFileInfo]) => {
                    const wopiInfo = this.getWopiInfo(wopiHostData.wac);
                    wopiPendingApp.Complete(wopiInfo, newFileInfo);
                }).catch(() => {
                    // TODO: Log Preload Miss
                });
            } else {
                this.sendBootError(KnownInstrumentationEvents.InitializeWopiPendingFailed, fail, observer);
            }
        });

        loadFunctionSubject.next(loadApp);
        loadFunctionSubject.complete();

        const finishLoad = (wopiHostData, newFileInfo) => resolveWopiData([wopiHostData, newFileInfo]);
        const cancelLoad = rejectWopiData;

        const data: WacPendingLoadData = {
            bootstrapperObservable: loadFunctionSubject.asObservable(),
            fileNamePromise: wopiHostDataPromise.then(([wopiData]) => wopiData.fileName)
        };

        const loadData: BootAndLoadInfo = {
            correlationId,
            bootedDocumentInfo: {
                bootId,
                documentInfo: {
                    fileType,
                    wdOrigin
                }
            },
            data
        };

        const prebootedApplication: PrebootedApplication = {
            finishLoad,
            cancelLoad,
            loadData,
            loadFunctionSubject,
            expirationTime: Date.now() + this.contextService.sharedContext.wacHostAppPrebootTimeout,
            correlationAndBoot: [correlationId, bootId]
        };

        this.prebootedApplications.set(fileType, prebootedApplication);

        return prebootedApplication;
    }

    /**
     * Initializes the appropriate wac bootstrapper and generates a load function that accepts a container to load the wac in.
     * @param loadFunctionSubject: Subject used to emit a function that can load a wac app in a container
     * @param documentInfo: Info needed to boot the document in the UI host
     * @param wopiHostData: Data necessary for loading the wac applications
     * @param correlationId: Unique id that identifies the session and the document opened within that session
     * @param clickTime: Time representing when the document was requested (MRU item click or direct nav time)
     * @param retry: Boolean indicating if we should attempt to get missing or incorrect wopiHostData
     * @returns A promise that resolves to a function that will load wac content into the given container
     */
    private bootstrapFile(
        loadFunctionSubject: AsyncSubject<WacLoadFunction>,
        bootId: number,
        documentInfo: DocumentInfo,
        wopiHostData: WopiHostData,
        correlationId: string,
        clickTime: number,
        fetchDiagnostics: WacPerformanceTiming,
        retry: boolean = true,
        sessionIdToUse?: string
    ): void {
        const { fileType } = documentInfo;
        const wac = wopiHostData?.wac;
        let accessTokenExpiry = 0;
        try {
            accessTokenExpiry = wac?.accessTokenExpiry ? parseInt(wac.accessTokenExpiry) : 0;
        } catch {
            // TODO: Do nothing and we will retry below
        }

        if (!wac?.bootstrapperUrl || (accessTokenExpiry < Date.now())) {
            // If we never got the wopi data fetch that now.
            if (retry) {
                this.getWopiData(documentInfo, documentInfo.newFile, true).then(data => {
                    if (data) {
                        this.bootstrapFile(loadFunctionSubject, bootId, documentInfo, data.wopiHostData, correlationId, clickTime, data.fetchDiagnostics, false);
                    } else {
                        const error = 'Error occurred while trying to fetch wopi data';
                        this.sendBootError(KnownInstrumentationEvents.WacDocumentGenericBootError, error, loadFunctionSubject);
                    }
                }).catch((error: string) => {
                    this.sendBootError(KnownInstrumentationEvents.WacDocumentVroomError, error, loadFunctionSubject);
                });
            } else {
                const error = 'Failed to get wopi data for the item';
                this.sendBootError(KnownInstrumentationEvents.WacDocumentVroomError, error, loadFunctionSubject);
            }
            return;
        }
        const bootstrapperUrl = wopiHostData.wac.bootstrapperUrl;
        const applicationUrl = wopiHostData.wac.applicationUrl;

        this.logPerformanceEventFromClick(bootId, WacContentPerformanceMarker.DocumentBootstrapperLoadStart, fileType);
        this.loadBootstrapper(bootstrapperUrl, applicationUrl, fileType)
            .then(() => {
                const [bootstrapper, error] = this.getOfficeBootstrapper(fileType);

                if (error) {
                    this.sendBootError(KnownInstrumentationEvents.WacDocumentBootstrapperLoadError, error, loadFunctionSubject);
                    return;
                }

                this.logPerformanceEventFromClick(bootId, WacContentPerformanceMarker.DocumentBootstrapperLoadEnd, fileType);

                const wopiInfo = this.getWopiInfo(wopiHostData.wac);
                const loadApp = ({ container, flushTime, loadCorrelationId }: WacLoadParams) => new Observable<WacLoadData>(observer => {
                    const timeout = setTimeout(() => {
                        // We only log a "timeout", but do not kill the boot flow with the timeout
                        this.logError(
                            correlationId,
                            documentInfo,
                            KnownInstrumentationEvents.WacDocumentJsApiTimeout
                        );
                    }, 150000);

                    const repeatLoad = correlationId !== loadCorrelationId;
                    const initParams = this.getInitParams(
                        documentInfo.fileType,
                        loadCorrelationId,
                        documentInfo.wdOrigin,
                        container.nativeElement,
                        repeatLoad ? flushTime : clickTime,
                        flushTime,
                        (officeApplication, wacBootDiagnostics) => {
                            clearTimeout(timeout);
                            this.logPerformanceEventFromClick(bootId, WacContentPerformanceMarker.DocumentLoadEnd, fileType);
                            this.logBootPerformance(correlationId, documentInfo, wacBootDiagnostics, clickTime);
                            observer.next({
                                wopiHostData,
                                officeApplication
                            });
                            observer.complete();
                        },
                        fail => {
                            clearTimeout(timeout);
                            this.sendBootError(KnownInstrumentationEvents.WacDocumentJsApiError, fail, observer);
                        },
                        documentInfo.docId,
                        wopiHostData,
                        wopiInfo,
                        bootstrapperUrl,
                        fetchDiagnostics,
                        documentInfo.applicationUrlParams,
                        documentInfo.rebootOverrideData
                    );

                    this.logPerformanceEventFromClick(bootId, WacContentPerformanceMarker.DocumentInitializeWopiPending, fileType);
                    const wopiPendingApp = bootstrapper.Application.InitializeWopiPending(initParams, undefined, sessionIdToUse);

                    if (wopiPendingApp) {
                        wopiPendingApp.Complete(wopiInfo);
                    } else {
                        this.sendBootError(KnownInstrumentationEvents.InitializeWopiPendingFailed, fail, observer);
                    }
                });
                loadFunctionSubject.next(loadApp);
                loadFunctionSubject.complete();
            })
            .catch(() => {
                const error = 'Failed to load the bootstrapper';
                this.sendBootError(KnownInstrumentationEvents.WacDocumentBootstrapperLoadError, error, loadFunctionSubject);
            });
    }

    private getOfficeBootstrapper(fileType: FileType): [OfficeBootstrapper, string] {
        const microsoft = this.windowService.nativeWindow.Microsoft;
        if (typeof microsoft === 'undefined') {
            return [null, 'Microsoft is undefined'];
        }

        const bootstrapperContainer: OfficeBootstrapperContainer = microsoft.Office;
        const appType = FileType[fileType];
        if (!bootstrapperContainer || !bootstrapperContainer[appType]) {
            return [null, `Failed to find bootstrapper for app: ${appType}`];
        }

        return [bootstrapperContainer[appType], null];
    }

    /* Wac Initialization Param Code */
    private getSessionContext(): any {
        return {
            pmo: this.windowService.nativeWindow.location.origin, // post message origin
            pmshare: true, // support post message on share button click
            surl: undefined, // probably not required
            curl: undefined, // probably not required
            vurl: undefined, // probably not required
            eurl: this.windowService.nativeWindow.location.href
        };
    }

    private getSessionInformation(userId: string, correlationId, wdParams: Params = {}): OfficeBootstrapperSessionInfo {
        const market = this.contextService.sharedContext.officeMarket;
        const capabilities = this.capabilitiesService.capabilities;
        return {
            HostName: 'office.com',
            HostSessionId: correlationId,
            UserId: userId,
            OwnerId: undefined,
            UiLocale: market,
            DataLocale: market,
            DisableChat: true,
            CookieCompliant: true,
            WdParams: wdParams, // used to pass-though the wd-params. not sure we really needed, but let's keep it
            DisableFocusOnBoot: false,
            SessionContext: JSON.stringify(this.getSessionContext()),
            HideHeader: this.capabilitiesService.capabilities.isEmbedMode,
            // tslint:disable-next-line
            SupportsDocReboot: DocRebootSupportMode.AnyDoc | DocRebootSupportMode.OtherMode,
            SupportsCreateNewDocument: true,
            SupportsAuthToken: capabilities.wacHostGetAuthTokenEnabled,
            UiHostIntegrationType: this.uihit,
            SupportsAtMentions: true,
            SupportsCustomAuthConfig: capabilities.wacHostGetCustomAuthConfigEnabled,
            SupportsExcelExternalLinks: capabilities.wacHostExternalLinkEnabled,
            SupportsMoveToFolder: capabilities.wacHostMoveToFolderEnabled
        };
    }

    private getOrCreateDefaultPrecheckInfo(wopiData?: WopiHostData, docId?: string): OfficeBootstrapperWopiPrecheckInfo {
        return wopiData
            ? {
                FileName: wopiData.fileName,
                FileGetUrl: wopiData.downloadUrl || wopiData.wac.fileGetUrl, // We should be able to use the wac.fileGetUrl, but there is a bug
                FileSize: wopiData.fileSize,
                // TODO: If bundleUrl is undefined, should we pass null BundleInfo or null props?
                BundleInfo: {
                    Url: wopiData.bundleUrl,
                    MajorVersion: wopiData.bundleVersion
                },
                ReadOnly: wopiData.readOnly ?? false,
                IrmEnabled: wopiData.irmEnabled ?? false,
                ETag: wopiData.eTag,
                ClientThrottlingProtection: wopiData.wac.clientThrottlingProtection,
                RequestedCallThrottling: wopiData.wac.requestedCallThrottling,
                IsNewFile: wopiData.isNewFile,
                ...(docId && {
                    DocUniqueId: docId
                })
            } : {
                FileName: ''
            };
    }

    private getWopiInfo(wopiData: Wopi): WopiInfo {
        return {
            WopiSrc: wopiData.wopiSrc,
            AccessToken: wopiData.accessToken,
            AccessTokenExpiry: new Date(parseInt(wopiData.accessTokenExpiry))
        };
    }

    private getDiagnostics(
        userClickTime: number,
        flushTime: number,
        wdOrigin: string,
        fetchDiagnostics: WacPerformanceTiming = { startTime: 0, endTime: 0 },
        bootstrapperUrl?: string
    ): HostInitializeDiagnostics {
        const bootDiagnostics = bootstrapperUrl ? this.bootstrapperDiagnostics.get(bootstrapperUrl) : { startTime: 0, endTime: 0 };

        // These markers have to be in a specific order with the time being equal or greater than the previous marker.
        // We do some of these steps before user click so we just make it the same value as the previous step to prevent invalid data.
        const hostPageNavigationStartTime = Math.max(this.performanceService.navigationStart, userClickTime);
        const hostPageFetchStartTime = Math.max(fetchDiagnostics.startTime ? fetchDiagnostics.startTime : 0, hostPageNavigationStartTime);
        const hostPageFetchEndTime = Math.max(fetchDiagnostics.endTime ? fetchDiagnostics.endTime : 0, hostPageFetchStartTime);
        const hostPageFirstFlushTime = Math.max(flushTime, hostPageFetchEndTime);
        const officeBootstrapperStartTime = Math.max(bootDiagnostics ? bootDiagnostics.startTime : 0, hostPageFirstFlushTime);
        const officeBootstrapperEndTime = Math.max(bootDiagnostics ? bootDiagnostics.endTime : 0, officeBootstrapperStartTime);
        const officeInitializationTime = Date.now();

        return {
            entryPoint: wdOrigin,
            userClickTime,
            hostPageNavigationStartTime,
            hostPageFetchStartTime,
            hostPageFetchEndTime,
            hostPageFirstFlushTime,
            officeBootstrapperStartTime,
            officeBootstrapperEndTime,
            officeInitializationTime
        };
    }

    private getInitParams(
        fileType: FileType,
        correlationId: string,
        wdOrigin: string,
        container: HTMLElement,
        clickTime: number,
        flushTime: number,
        successCallback: OfficeBootstrapperSuccessCallback,
        failureCallback: OfficeBootstrapperFailureCallback,
        docId?: string,
        wopiData?: WopiHostData,
        wopiInfo?: any,
        specificBootstrapperUrl?: string,
        fetchDiagnostics?: WacPerformanceTiming,
        additionalAppParams?: Params,
        rebootOverrideData?: RebootOverrideData
    ): OfficeBootstrapperInitParams {
        const customSettings: OfficeApplicationCustomSettings = {
            HostUIControls: [
                { id: 'close', visibility: 0 },
                { id: 'share', visibility: 1 }
            ]
        };

        const { bootstrapperUrl, applicationUrl } = this.applicationUrls.get(fileType);

        const ApplicationUrlBase = wopiData ? wopiData.wac.applicationUrl || wopiData.wac.editUrl : UrlBuilder.removeQueryParams(applicationUrl, ['wopisrc'], false);
        const applicationParams: Params = { ...additionalAppParams, uiEmbed: 1, uih: this.uih };
        const ApplicationUrl = UrlBuilder.appendQueryParams(ApplicationUrlBase, applicationParams);
        const wdParams: Params = {};
        const scenarioId = this.capabilitiesService.capabilities.scenarioId;
        if (scenarioId) {
            wdParams['wdscenarioId'] = scenarioId;
        }

        return {
            BootstrapperUrl: specificBootstrapperUrl ? specificBootstrapperUrl : bootstrapperUrl,
            Container: container, // whatever HTML Element
            SessionInformation: this.getSessionInformation(
                wopiData ? wopiData.wac.userId : this.clientStartupConfigService.config.puid,
                correlationId,
                wdParams
            ),
            ApplicationUrl,
            WopiPrecheckInfo: this.getOrCreateDefaultPrecheckInfo(wopiData, docId),
            ApplicationCustomSettings: customSettings,
            Diagnostics: this.getDiagnostics(clickTime, flushTime, wdOrigin, fetchDiagnostics, bootstrapperUrl),
            FnOnInitializeSuccess: successCallback,
            FnOnInitializeFailure: failureCallback,
            FnIsFeatureEnabled: this.createIsFeatureEnabledCallback(fileType),
            RebootOverrideData: rebootOverrideData,
            ...(wopiInfo ? { WopiInfo: wopiInfo } : {})
        };
    }

    private createIsFeatureEnabledCallback(fileType: FileType): IsFeatureEnabledCallback {
        return (featureName: string) => {
            switch (featureName) {
                case 'OfficeComUiHost': // this is Office web signal to jsapi
                    return !this.isStandalone && !this.capabilitiesService.capabilities.isPwaInUwp;
                case 'OfficeHwaUiHost': // this is Office hwa signal to jsapi
                    return this.capabilitiesService.capabilities.isPwaInUwp;
                case 'OfficeComPreboot': // this is office onhover preboot signal to jsapi.
                    // returns true if in Treatment, false if in control and undefined if in neither of those.
                    return (this.capabilitiesService.wacHostEnabledForFileType(fileType) && this.capabilitiesService.wacHostHoverPrebootEnabledForFileType(fileType)) ? true
                    : this.capabilitiesService.capabilities.wacHostHoverPrebootControlFlight ? false : undefined;
                default:
                    return undefined;
            }
        };
    }

    private logError(
        correlationId: string,
        documentInfo: DocumentInfo,
        errorEvent: KnownInstrumentationEvents,
        errorMessage?: string
    ): void {
        const additionalParams: InstrumentationParams = {
            [InstrumentationParam.Error]: errorMessage
        };

        this.instrumentationService.logErrorEvent(
            errorEvent,
            createWacContentInstrumentationParams(correlationId, documentInfo, additionalParams)
        );
    }

    private sendBootError(instrumentationEvent: WacHostBootErrorEvent, error: any, loadSubject: Subject<any> | Subscriber<any>) {
        const bootError: WacHostBootError = {
            instrumentationEvent,
            error
        };

        loadSubject.error(bootError);
    }

    private getUihit(isWebPwa: boolean = false): string {
        if (this.capabilitiesService.capabilities.isPwaInUwp) {
            return WacBootstrapperService.uihitOfficeComHwa;
        } else if (isWebPwa) {
            return WacBootstrapperService.uihitOfficeComPwa;
        }
        return '';
    }

    private getPrebootUrl(applicationUrl: string): string {
        if (!applicationUrl) {
            return null;
        }

        const parsedUrl = UrlParser.getLocation(applicationUrl);
        const prebootPath = parsedUrl.path.substring(0, parsedUrl.path.lastIndexOf('/')) + '/preboot.ashx';
        return UrlParser.getOrigin(applicationUrl) + prebootPath;
    }

    private getPrebootKey(documentInfo: DocumentInfo): string {
        return documentInfo.driveId + '_' + documentInfo.docId;
    }
}
